import {Card, Table, TableRow, type Column} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {VehicleSelectionPreviewItemResponseBody} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {useBranches} from '@dms/shared';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {formatVehicleSelectionVehicleTitle} from '../utils/formatVehicleSelectionVehicleTitle';
import {
  VehicleSelectionOutcomeList,
  type VehicleSelectionOutcomeValueFormatter,
} from './VehicleSelectionOutcomeList';

interface VehicleSelectionPreviewListProps extends RequiredTestIdProps {
  title: string;
  items: VehicleSelectionPreviewItemResponseBody[];
  // The whole count; the BE returns at most the preview limit of items.
  count: number;
  formatOutcomeValue: VehicleSelectionOutcomeValueFormatter;
}

export function VehicleSelectionPreviewList(props: VehicleSelectionPreviewListProps) {
  const {getBranchById} = useBranches();

  const columns: Column[] = [
    {
      element: (
        <Text size="small" color="secondary" align="left">
          {i18n.t('general.labels.vehicle')}
        </Text>
      ),
    },
    {
      element: (
        <Text size="small" color="secondary" align="left">
          {i18n.t('entity.branch.labels.branch')}
        </Text>
      ),
    },
    {
      element: (
        <Text size="small" color="secondary" align="left">
          {i18n.t('entity.interest.labels.conditions')}
        </Text>
      ),
    },
  ];

  const getBranchName = (branchId: string | null) =>
    isNotNil(branchId) ? getBranchById(branchId)?.marketingName : null;

  return (
    <Card
      title={`${props.title} (${props.count})`}
      variant="inlineGrey"
      data-testid={props['data-testid']}
    >
      <VStack spacing={3}>
        <Table columns={columns} data-testid={suffixTestId('table', props)}>
          {props.items.map((item, index) => (
            <TableRow key={item.saleVehicleId} data-testid={suffixTestId(`item-[${index}]`, props)}>
              <Text size="small" alternative>
                {formatVehicleSelectionVehicleTitle(item)}
              </Text>
              <Text size="small">{getBranchName(item.branchId) ?? EMPTY_PLACEHOLDER}</Text>
              <VehicleSelectionOutcomeList
                outcomes={item.outcomes}
                formatValue={props.formatOutcomeValue}
                data-testid={suffixTestId(`item-[${index}]-outcomes`, props)}
              />
            </TableRow>
          ))}
        </Table>
        <Show when={props.items.length < props.count}>
          <Text size="xSmall" color="tertiary" data-testid={suffixTestId('limited', props)}>
            {i18n.t('entity.promoPhotos.preview.labels.showingFirst', {
              shown: props.items.length,
              count: props.count,
            })}
          </Text>
        </Show>
      </VStack>
    </Card>
  );
}
